import {
  Brain,
  Gauge,
  LayoutPanelTop,
  type LucideIcon,
  Rocket,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  Sparkles,
} from "lucide-react";
import type { Category, RiskLevel } from "@/lib/skills/types";

/** Fallback install steps shown when a skill hasn't set its own. Mirrors the
 * generic Claude Code install flow the catalog assumed before steps were editable. */
export const DEFAULT_INSTALL_STEPS: string[] = [
  "Run the command above in your Claude Code project root.",
  "The skill registers under .claude/skills/ and becomes available to the agent.",
  "Invoke it by name, or let Claude pick it up automatically when the scenario matches.",
];

interface CategoryMeta {
  tint: string;
  bg: string;
  fg: string;
  icon: LucideIcon;
}

export const CATEGORIES: Record<Category, CategoryMeta> = {
  "UI/UX": { tint: "#5461AD", bg: "#F2F0FB", fg: "#4B3B87", icon: LayoutPanelTop },
  Security: { tint: "#1E6CC7", bg: "#E8F2FB", fg: "#1555A3", icon: ShieldCheck },
  Efficiency: { tint: "#2D9BD6", bg: "#E6F4FB", fg: "#1C6E9C", icon: Gauge },
};

export function categoryMeta(c: Category): CategoryMeta {
  return CATEGORIES[c];
}

interface RiskMeta {
  label: string;
  icon: LucideIcon;
  rank: number;
  /** Tailwind classes for the badge surface (fg + bg). */
  className: string;
}

export const RISK: Record<RiskLevel, RiskMeta> = {
  vetted: { label: "Vetted", icon: ShieldCheck, rank: 0, className: "bg-success-bg text-success" },
  low: { label: "Low risk", icon: Shield, rank: 1, className: "bg-info-bg text-info" },
  review: { label: "Review", icon: ShieldAlert, rank: 2, className: "bg-warning-bg text-warning" },
  high: { label: "High risk", icon: ShieldX, rank: 3, className: "bg-danger-bg text-danger" },
};

export function riskMeta(level: RiskLevel): RiskMeta {
  return RISK[level];
}

/** review + high count as "flagged" for moderation. */
export function isFlagged(level: RiskLevel): boolean {
  return level === "review" || level === "high";
}

interface Scenario {
  id: string;
  label: string;
  icon: LucideIcon;
  skills: string[]; // skill slugs
}

export const SCENARIOS: Scenario[] = [
  {
    id: "new-build",
    label: "Starting a new app",
    icon: Rocket,
    skills: ["prd-to-scaffold", "design-token-extractor", "api-mock-server"],
  },
  {
    id: "ship-safe",
    label: "Shipping to production",
    icon: ShieldCheck,
    skills: ["secrets-scanner", "dep-cve-audit", "test-suite-generator"],
  },
  {
    id: "polish-ui",
    label: "Polishing the UI",
    icon: Sparkles,
    skills: ["a11y-linter", "responsive-auditor", "component-storybook"],
  },
  {
    id: "ai-feature",
    label: "Adding an AI feature",
    icon: Brain,
    skills: ["prompt-injection-shield", "secrets-scanner"],
  },
];

/** Transparent, rule-based recommendation reason (no AI inference). */
export function reasonFor(s: {
  rating: number;
  risk: RiskLevel;
  downloads: number;
  updatedDaysAgo: number;
}): string {
  if (s.rating >= 4.7 && s.risk === "vetted") return "Highly rated · Vetted";
  if (s.downloads >= 400) return "Most downloaded in category";
  if (s.updatedDaysAgo <= 14) return "Recently updated";
  if (s.risk === "vetted") return "Vetted · safe to use";
  return "Strong match for your scenario";
}
